import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import type { BolaoDetail } from '@/types/bolao';
import { useAuth } from '@/contexts/AuthContext';

/**
 * BolaoContext — Mantém referência ao bolão ativo selecionado pelo usuário.
 *
 * Persiste o ID do último bolão selecionado em localStorage (key: bolao_active_bolao).
 * Calcula isAdmin comparando user.userId com activeBolao.adminId.
 */

const STORAGE_KEY = 'bolao_active_bolao';

interface BolaoContextType {
  activeBolao: BolaoDetail | null;
  setActiveBolao(bolao: BolaoDetail): void;
  clearActiveBolao(): void;
  isAdmin: boolean;
  /** Incrementado após ações mutantes (sair, deletar, salvar palpite) para forçar re-fetch nas listas */
  refreshKey: number;
  triggerRefresh(): void;
}

const BolaoContext = createContext<BolaoContextType | null>(null);

function loadStoredBolao(): BolaoDetail | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;

  try {
    return JSON.parse(stored) as BolaoDetail;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function BolaoProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [activeBolao, setActiveBolaoState] = useState<BolaoDetail | null>(
    loadStoredBolao,
  );
  const [refreshKey, setRefreshKey] = useState(0);

  const triggerRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  // Sync from localStorage on mount
  useEffect(() => {
    const stored = loadStoredBolao();
    if (stored) {
      setActiveBolaoState(stored);
    }
  }, []);

  const setActiveBolao = useCallback((bolao: BolaoDetail) => {
    setActiveBolaoState(bolao);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bolao));
  }, []);

  const clearActiveBolao = useCallback(() => {
    setActiveBolaoState(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const isAdmin = useMemo(() => {
    if (!user || !activeBolao) return false;
    // SUPERADMIN tem acesso admin a todos os bolões
    if (user.roles?.includes('SUPERADMIN')) return true;
    // Backend retorna adminUserId, frontend type usa adminId — verificar ambos
    return user.userId === activeBolao.adminId || user.userId === activeBolao.adminUserId;
  }, [user, activeBolao]);

  return (
    <BolaoContext.Provider
      value={{ activeBolao, setActiveBolao, clearActiveBolao, isAdmin, refreshKey, triggerRefresh }}
    >
      {children}
    </BolaoContext.Provider>
  );
}

export function useBolao(): BolaoContextType {
  const context = useContext(BolaoContext);
  if (!context) {
    throw new Error('useBolao deve ser usado dentro de BolaoProvider');
  }
  return context;
}
